import { type CourierView, type Order, type OrderStatus, type Refund } from './orders-api';

/**
 * How the Orders frame's words and colours map onto the platform's own order
 * record. Pure functions, so every mapping is pinned by a test.
 *
 * The frame is a prototype: its orders walk themselves through five steps on a
 * twenty-second timer, carry a courier called Sam, an ETA, a Visa card and a
 * hash-generated collection code. None of that is used. Every word here is
 * chosen from what the order actually says — its status, its own history of
 * when it reached each status, its payment status, the refunds recorded on it
 * and the courier job, if there is one.
 */

export const TINTS = ['#f0ecfa', '#fdeef0', '#e9f6ee', '#e7f6fa', '#fdf2e4', '#eeeafb'] as const;

/** The frame's three shop tiles. Which one a shop gets is fixed by its id, not by anything it claims. */
const TILES = [
  'linear-gradient(145deg,#6d3ffb,#4a22bd)',
  'linear-gradient(145deg,#1bafd6,#0d7d9c)',
  'linear-gradient(145deg,#1f9d6b,#0e6f5c)',
] as const;

export function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export const tintFor = (name: string): string => TINTS[hash(name) % TINTS.length] ?? TINTS[0];

export function shopOf(order: Order): { name: string; initials: string; tile: string } {
  const name = order.storefront?.name ?? 'The pharmacy';
  const words = name
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w !== '' && !/^(the|and|of)$/i.test(w));
  const [first = '', second = ''] = words;
  const initials = (
    second === '' ? first.slice(0, 2) : `${first.slice(0, 1)}${second.slice(0, 1)}`
  ).toUpperCase();
  return {
    name,
    initials: initials === '' ? 'JT' : initials,
    tile: TILES[hash(order.storefrontId) % TILES.length] ?? TILES[0],
  };
}

export function money(minor: number, currency = 'GBP'): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `£${(minor / 100).toFixed(2)}`;
  }
}

export function time(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function day(iso: string, now = new Date()): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** When the order reached a status, from its own history. The latest entry wins. */
export function reachedAt(order: Order, status: OrderStatus): string | undefined {
  for (let i = order.history.length - 1; i >= 0; i -= 1) {
    const entry = order.history[i];
    if (entry?.status === status) return entry.at;
  }
  return undefined;
}

export const isFinal = (order: Order): boolean =>
  order.status === 'COMPLETED' || order.status === 'CANCELLED';

export const isDelivery = (order: Order): boolean => order.fulfilment === 'DELIVERY';

export const itemCount = (order: Order): number => order.lines.reduce((sum, line) => sum + line.quantity, 0);

export interface Badge {
  readonly label: string;
  readonly bg: string;
  readonly fg: string;
}

/** The frame's badge per state, worded for what the platform's status means. */
export function badgeOf(order: Order): Badge {
  if (order.status === 'CANCELLED') return { label: 'CANCELLED', bg: '#fbe3e3', fg: '#8c1919' };
  if (order.payment.status === 'REFUNDED') return { label: 'REFUNDED', bg: '#efeaf9', fg: '#55506b' };
  switch (order.status) {
    case 'PLACED':
      return { label: 'WAITING ON PAYMENT', bg: '#f0ecfa', fg: '#4a22bd' };
    case 'AWAITING_REVIEW':
      return { label: 'WITH THE PRESCRIBER', bg: '#e7f6fa', fg: '#0d5d73' };
    case 'CONFIRMED':
      return { label: 'CONFIRMED', bg: '#f0ecfa', fg: '#4a22bd' };
    case 'PREPARING':
      return { label: 'PREPARING', bg: '#fdf2e4', fg: '#8c4a06' };
    case 'OUT_FOR_DELIVERY':
      return { label: 'OUT FOR DELIVERY', bg: '#f0ecfa', fg: '#4a22bd' };
    case 'READY_FOR_COLLECTION':
      return { label: 'READY FOR COLLECTION', bg: '#ddf2e7', fg: '#0b5a4a' };
    case 'COMPLETED':
      return { label: isDelivery(order) ? 'DELIVERED' : 'COLLECTED', bg: '#ddf2e7', fg: '#0b5a4a' };
  }
}

const CANCELLED_BY: Record<string, string> = {
  CUSTOMER: 'Cancelled by you',
  PROVIDER: 'Cancelled by the pharmacy',
};

export function cancelledBy(order: Order): string {
  return CANCELLED_BY[order.cancellation?.by ?? ''] ?? 'Cancelled';
}

/** Whether money was taken for it. Only the payment provider's webhook sets PAID. */
export const wasPaid = (order: Order): boolean =>
  order.payment.status === 'PAID' || order.payment.status === 'REFUNDED';

export interface Strip {
  readonly text: string;
  readonly bg: string;
  readonly fg: string;
  readonly icon: 'truck' | 'store' | 'card' | 'warn' | 'check';
}

/** The coloured line on a card. None on a finished order that went through, as on the frame. */
export function stripOf(order: Order): Strip | undefined {
  const delivery = isDelivery(order);
  if (order.status === 'CANCELLED') {
    return {
      text: wasPaid(order) ? cancelledBy(order) : `${cancelledBy(order)} · nothing charged`,
      bg: '#fbe3e3',
      fg: '#8c1919',
      icon: 'warn',
    };
  }
  if (order.payment.status === 'REFUNDED') {
    return { text: 'Payment refunded', bg: '#f7f5fc', fg: '#55506b', icon: 'card' };
  }
  switch (order.status) {
    case 'PLACED':
      return {
        text: 'Waiting on payment · confirmed once it goes through',
        bg: '#f0ecfa',
        fg: '#4a22bd',
        icon: 'card',
      };
    case 'AWAITING_REVIEW':
      return {
        text: 'Paid · the prescriber is reviewing your answers',
        bg: '#e7f6fa',
        fg: '#0d5d73',
        icon: 'check',
      };
    case 'CONFIRMED':
      return {
        text: 'Paid · with the pharmacy',
        bg: '#f0ecfa',
        fg: '#4a22bd',
        icon: delivery ? 'truck' : 'store',
      };
    case 'PREPARING':
      return {
        text: 'The pharmacy is preparing it',
        bg: '#f0ecfa',
        fg: '#4a22bd',
        icon: delivery ? 'truck' : 'store',
      };
    case 'OUT_FOR_DELIVERY': {
      const since = reachedAt(order, 'OUT_FOR_DELIVERY');
      return {
        text: since === undefined ? 'Out for delivery' : `Out for delivery since ${time(since)}`,
        bg: '#f0ecfa',
        fg: '#4a22bd',
        icon: 'truck',
      };
    }
    case 'READY_FOR_COLLECTION':
      return {
        text: 'Ready to collect · bring your order number',
        bg: '#ddf2e7',
        fg: '#0b5a4a',
        icon: 'store',
      };
    case 'COMPLETED':
      return undefined;
  }
}

/** The card's main button, as the frame words it. */
export function primaryOf(order: Order): string {
  if (isFinal(order)) return 'View order';
  return order.status === 'READY_FOR_COLLECTION' ? 'Show collection code' : 'Track order';
}

export interface Step {
  readonly label: string;
  readonly sub: string;
  readonly state: 'done' | 'current' | 'todo';
}

const STEP_LABEL: Record<OrderStatus, (delivery: boolean) => string> = {
  PLACED: () => 'Order placed',
  AWAITING_REVIEW: () => 'Prescriber review',
  CONFIRMED: () => 'Order confirmed',
  PREPARING: () => 'Being prepared',
  OUT_FOR_DELIVERY: () => 'Out for delivery',
  READY_FOR_COLLECTION: () => 'Ready to collect',
  COMPLETED: (delivery) => (delivery ? 'Delivered' : 'Collected'),
  CANCELLED: () => 'Cancelled',
};

/**
 * The timeline: "Order placed", then the platform's own journey for this order
 * (`GET …/tracking`), each step done, current or still to come. A step that is
 * done says when, from the order's history. A step to come says nothing it
 * cannot know — no "usually within 10 minutes", no ETA.
 */
export function stepsOf(order: Order, journey: readonly OrderStatus[]): readonly Step[] {
  const shop = order.storefront?.name ?? 'the pharmacy';
  const delivery = isDelivery(order);
  const all: readonly OrderStatus[] = ['PLACED', ...journey.filter((s) => s !== 'PLACED')];

  if (order.status === 'CANCELLED') {
    const cancelledAt = order.cancellation?.at ?? reachedAt(order, 'CANCELLED');
    return [
      { label: 'Order placed', sub: `${day(order.placedAt)}, ${time(order.placedAt)}`, state: 'done' },
      {
        label: 'Cancelled',
        sub: cancelledAt === undefined ? cancelledBy(order) : `${cancelledBy(order)} · ${time(cancelledAt)}`,
        state: 'current',
      },
    ];
  }

  const at = all.indexOf(order.status);
  const finished = order.status === 'COMPLETED';
  const current: Record<string, string> = {
    PLACED: 'Waiting on payment. It is confirmed once the payment goes through.',
    AWAITING_REVIEW: 'The prescriber is reviewing your answers',
    CONFIRMED: `Waiting for ${shop} to start on it`,
    PREPARING: 'Picking and packing your items',
    OUT_FOR_DELIVERY: 'Your courier is on the way',
    READY_FOR_COLLECTION: 'Waiting at the counter',
  };
  const todo: Record<string, string> = {
    PREPARING: `Picked and packed by ${shop}`,
    COMPLETED: delivery ? '' : 'Show your order number at the counter',
  };

  return all.map((status, index) => {
    const reached = reachedAt(order, status);
    const done = index < at || (finished && index === at);
    if (done) {
      const sub =
        reached === undefined
          ? ''
          : status === 'COMPLETED' || status === 'PLACED'
            ? `${day(reached)}, ${time(reached)}`
            : `Done at ${time(reached)}`;
      return { label: STEP_LABEL[status](delivery), sub, state: 'done' };
    }
    if (index === at)
      return { label: STEP_LABEL[status](delivery), sub: current[status] ?? '', state: 'current' };
    return { label: STEP_LABEL[status](delivery), sub: todo[status] ?? '', state: 'todo' };
  });
}

export interface Hero {
  readonly title: string;
  readonly sub: string;
}

/** The detail page's headline and the line under it. */
export function heroOf(order: Order, refunds: readonly Refund[]): Hero {
  const shop = order.storefront?.name ?? 'The pharmacy';
  if (order.status === 'CANCELLED') {
    return {
      title: 'Order cancelled',
      sub: wasPaid(order) ? cancelledBy(order) : "You haven't been charged",
    };
  }
  const refunded = completedRefundMinor(refunds);
  if (refunded > 0 && order.status === 'COMPLETED') {
    return {
      title: refunded >= order.totalMinor ? 'Refunded' : 'Partially refunded',
      sub: `${money(refunded, order.currency)} refunded`,
    };
  }
  switch (order.status) {
    case 'PLACED':
      return { title: 'Order placed', sub: 'It is confirmed once the payment goes through.' };
    case 'AWAITING_REVIEW':
      return {
        title: 'The prescriber is checking your order',
        sub: 'They review your answers before the pharmacy prepares it',
      };
    case 'CONFIRMED':
      return { title: 'Order received', sub: `Paid · waiting for ${shop} to start on it` };
    case 'PREPARING':
      return { title: 'Being prepared', sub: `${shop} is picking and packing your items` };
    case 'OUT_FOR_DELIVERY': {
      const since = reachedAt(order, 'OUT_FOR_DELIVERY');
      return {
        title: 'On its way',
        sub: since === undefined ? 'Out for delivery' : `Out for delivery since ${time(since)}`,
      };
    }
    case 'READY_FOR_COLLECTION':
      return { title: 'Ready to collect', sub: shop };
    case 'COMPLETED': {
      const doneAt = reachedAt(order, 'COMPLETED');
      if (!isDelivery(order)) return { title: 'Collected', sub: `Collected from ${shop}` };
      return {
        title: 'Delivered',
        sub: doneAt === undefined ? 'Delivered' : `Delivered ${day(doneAt)} at ${time(doneAt)}`,
      };
    }
  }
}

export function completedRefundMinor(refunds: readonly Refund[]): number {
  return refunds.filter((r) => r.status === 'COMPLETED').reduce((sum, r) => sum + r.amountMinor, 0);
}

/** The payment, as the platform knows it. No card is stored anywhere, so none is named. */
export function paymentWords(order: Order): string {
  switch (order.payment.status) {
    case 'PAID':
      return 'Paid';
    case 'FAILED':
      return 'Did not go through';
    case 'REFUNDED':
      return 'Refunded';
    default:
      return 'Not taken yet';
  }
}

const VEHICLE: Record<string, string> = {
  CAR: 'In a car',
  VAN: 'In a van',
  SCOOTER: 'On a scooter',
  BICYCLE: 'On a bike',
  E_BIKE: 'On an e-bike',
  ON_FOOT: 'On foot',
};

export function vehicleWords(vehicle: string): string {
  return VEHICLE[vehicle] ?? '';
}

/** The courier card shows only while a courier holds the order. */
export function activeCourier(view: CourierView | undefined): CourierView['courier'] {
  if (view === undefined || view.courier === null) return null;
  return view.status === 'ACCEPTED' || view.status === 'PICKED_UP' ? view.courier : null;
}

/**
 * The frame lets a customer cancel until the pharmacy starts preparing, and
 * the website keeps that rule. It is stricter than the platform (which also
 * accepts a cancel later), never looser; the platform still decides.
 */
export const cancellable = (order: Order): boolean =>
  order.status === 'PLACED' || order.status === 'AWAITING_REVIEW' || order.status === 'CONFIRMED';
